<template>
  <div>
    <KTCard ref="preview" v-bind:title="'Add new'">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-service-invoices' }">{{
            $t("MENU.ITEM.SERVICE.INVOICES")
          }}</router-link>
          <i class="mr-2"></i>
          <small class="">{{ $t("LABELS.ADD_NEW") }}</small>
          <span v-if="aiInvoiceId" class="badge badge-success ml-2">
            {{ $t("LABELS.FROM_AI_INVOICE") }}
          </span>
        </h3>
      </template>
      <template v-slot:toolbar>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingServiceInvoice"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
      <template v-slot:body>
        <AiInvoiceReferenceCard
          v-if="aiInvoiceData && !isLoadingAiInvoice"
          :aiInvoiceData="aiInvoiceData"
          class="mb-5"
        />

        <v-skeleton-loader
          v-if="isLoadingAiInvoice"
          type="card"
          class="mb-5"
        ></v-skeleton-loader>

        <ServiceInvoicesForm
          ref="ServiceInvoicesForm"
          :form="form"
          @add-row="handleAddRow"
        ></ServiceInvoicesForm>

        <v-overlay :value="!firstLoader && isLoadingServiceInvoice">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </template>
      <template v-slot:toolbarfoot>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingServiceInvoice"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
    </KTCard>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { SAVE_SERVICE_INVOICE } from "@/modules/service-invoices/store/service-invoices.module";

import SaveButtonDropdown from "@/view/content/forms/components/SaveButtonDropdown.vue";

import KTCard from "@/view/content/Card.vue";
import ServiceInvoicesForm from "../components/ServiceInvoicesForm.vue";
import AiInvoiceReferenceCard from "@/modules/ai-invoice-type-adapters/components/AiInvoiceReferenceCard.vue";

import { aiInvoiceFormMixin } from "@/modules/ai-invoice-type-adapters/mixins/aiInvoiceFormMixin.js";
import { serviceInvoiceAiMixin } from "@/modules/ai-invoice-type-adapters/mixins/serviceInvoiceAiMixin.js";

import { mapGetters } from "vuex";

export default {
  name: "ServiceInvoicesAdd",
  mixins: [aiInvoiceFormMixin, serviceInvoiceAiMixin],
  components: {
    KTCard,
    SaveButtonDropdown,
    ServiceInvoicesForm,
    AiInvoiceReferenceCard
  },
  data() {
    return {
      errors: {},
      firstLoader: true,
      form: {
        serviceVendorId: null,
        invoiceNumber: null,
        invoiceDate: null,
        qty: 1,
        totalNoVat: "0.00",
        totalWithVat: "0.00",
        vat: "0.00",
        toAccounting: true,
        rows: []
      }
    };
  },
  computed: {
    ...mapGetters(["isLoadingServiceInvoice"])
  },
  async mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.SERVICE.INVOICES"),
        route: { name: "list-service-invoices" }
      },
      { title: this.$i18n.t("LABELS.ADD_NEW") }
    ]);

    await this.loadServiceVendorDependencies();

    if (this.aiInvoiceId) {
      await this.loadAiInvoiceData();
    }

    this.firstLoader = false;
  },
  methods: {
    prefillFromAiInvoice() {
      if (!this.aiInvoiceData) return;

      this.prefillBasicInvoiceInfo(this.form);
      this.autoMatchServiceVendor(this.form);
      this.autoPopulateServiceRows(this.form);
    },

    handleAddRow() {
      this.$refs.ServiceInvoicesForm.addNewRow();
    },
    transformRowsForBackend(rows) {
      return rows.map(row => {
        const serviceExpenses = [];

        const expense = {
          quantity: parseFloat(row.totalQty) || 0,
          serviceWarehouseItemId: row.serviceWarehouseItemId || null,
          machineId: null,
          staffId: null
        };

        if (row.machine && row.machineId) {
          expense.machineId = row.machineId;

          if (row.machine.id && row.machine.id.lastUsedStaffId) {
            expense.staffId = row.machine.id.lastUsedStaffId;
          }
        }

        serviceExpenses.push(expense);

        return {
          id: row.id,
          name: row.name,
          code: row.code,
          price: parseFloat(row.price) || 0,
          totalQty: parseFloat(row.totalQty) || 0,
          totalPrice: parseFloat(row.totalPrice) || 0,
          serviceExpenses: serviceExpenses
        };
      });
    },
    onSave(next) {
      let vm = this;
      this.$refs.ServiceInvoicesForm.$v.form.$touch();
      if (this.$refs.ServiceInvoicesForm.$v.form.$anyError) {
        const errorMessages = [];

        if (this.$refs.ServiceInvoicesForm.$v.form.serviceVendorId.$error) {
          errorMessages.push(this.$t("FORM_LABELS.VENDOR"));
        }
        if (this.$refs.ServiceInvoicesForm.$v.form.invoiceNumber.$error) {
          errorMessages.push(this.$t("FORM_LABELS.INVOICE_NUMBER"));
        }
        if (this.$refs.ServiceInvoicesForm.$v.form.invoiceDate.$error) {
          errorMessages.push(this.$t("FORM_LABELS.DATE"));
        }

        const errorText =
          errorMessages.length > 0
            ? `${this.$t("ALERTS.FILL_FIELDS")}: ${errorMessages.join(", ")}`
            : this.$t("ALERTS.FILL_REQUIRED_FIELDS");

        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> " + this.$t("ALERTS.WARNING"),
          text: errorText
        });
        return;
      }

      if (!this.$refs.ServiceInvoicesForm.validateRows()) {
        const errorText =
          this.$t("ALERTS.EXPENSE_REQUIRED") ||
          "Each service row must have at least one expense with valid quantity";
        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> " + this.$t("ALERTS.WARNING"),
          text: errorText
        });
        return;
      }

      const payload = {
        serviceVendorId: this.form.serviceVendorId,
        invoiceNumber: this.form.invoiceNumber,
        invoiceDate: this.form.invoiceDate,
        qty: this.form.qty,
        totalNoVat: this.form.totalNoVat,
        totalWithVat: this.form.totalWithVat,
        vat: this.form.vat,
        toAccounting: this.form.toAccounting,
        rows: this.transformRowsForBackend(this.form.rows)
      };

      if (this.aiInvoiceId) {
        payload.aiInvoiceId = this.aiInvoiceId;
      }

      this.$store
        .dispatch(SAVE_SERVICE_INVOICE, payload)
        .then(data => {
          vm.errors = {};
          let itemEdit = data.data;
          vm.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });

          if (next == "continue") {
            this.$router.push({
              name: "edit-service-invoices",
              params: { id: itemEdit.id }
            });
          }
          if (next == "add-row") {
            this.$router.push({
              name: "edit-service-invoices",
              params: { id: itemEdit.id }
            });
          }
          if (next == "new") {
            this.$router.push({ name: "add-service-invoices" });
          }
          if (next == "exit") {
            this.$router.push({ name: "list-service-invoices" });
          }
        })
        .catch(response => {
          let errorText = response.data.message;

          const extractErrorMessages = (obj, messages = []) => {
            if (typeof obj === "string") {
              messages.push(obj);
            } else if (Array.isArray(obj)) {
              obj.forEach(item => extractErrorMessages(item, messages));
            } else if (typeof obj === "object" && obj !== null) {
              Object.values(obj).forEach(value =>
                extractErrorMessages(value, messages)
              );
            }
            return messages;
          };

          if (response.data && response.data.errors) {
            vm.errors = response.data.errors;

            const errorMessages = extractErrorMessages(response.data.errors);

            if (errorMessages.length > 0) {
              errorText = errorMessages.join(", ");
            }
          }

          vm.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: errorText
          });
        });
    }
  }
};
</script>
